import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { theme } from '../theme';
import { AMERICAN_COUNTRIES } from '../data/geo';
import LocationFields from './LocationFields';

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  backgroundColor: theme.colors.background,
  border: `1px solid ${theme.colors.border}`,
  borderRadius: '7px',
  color: theme.colors.text,
  fontSize: '1rem',
  boxSizing: 'border-box',
  outline: 'none',
};

const labelStyle = {
  display: 'block',
  color: theme.colors.textMuted,
  marginBottom: '0.4rem',
  fontSize: '0.9rem',
};

const TIMEZONES = Intl.supportedValuesOf('timeZone');

function Register() {
  const [username, setUsername] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [companyDescription, setCompanyDescription] = useState('');
  const [phone, setPhone] = useState('');
  const [timezone, setTimezone] = useState('America/Bogota');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  // Ubicación de la empresa (todos opcionales)
  const [pais, setPais] = useState('');
  const [location, setLocation] = useState({ department: '', city: '', district: '', address: '' });
  const onLocationChange = (field, value) => setLocation(prev => ({ ...prev, [field]: value }));
  const navigate = useNavigate();

  const canSubmit = !loading && acceptedTerms;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!acceptedTerms) return;
    setError('');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(username)) {
      setError('El usuario debe ser un correo electrónico válido (ej: nombre@dominio.com)');
      return;
    }
    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }
    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username,
          name,
          password,
          company_name: companyName,
          company_description: companyDescription,
          phone,
          timezone,
          pais,
          department: location.department,
          city: location.city,
          district: location.district,
          address: location.address,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message);
        return;
      }
      navigate('/login');
    } catch {
      setError('Error de conexión. Verifica que el servidor esté corriendo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main style={{ padding: '3rem 1.5rem', display: 'flex', justifyContent: 'center' }}>
      <div style={{
        backgroundColor: theme.colors.surface,
        borderRadius: '14px',
        border: `1px solid ${theme.colors.border}`,
        padding: '2.5rem',
        width: '100%',
        maxWidth: '420px',
        boxShadow: `0 4px 24px ${theme.colors.glowSubtle}`,
      }}>
        <h1 style={{ color: theme.colors.primary, marginBottom: '2rem', textAlign: 'center', fontSize: '1.6rem' }}>
          Crear Cuenta
        </h1>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1.2rem' }}>
            <label style={labelStyle}>Nombre</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nombre que verán los clientes al agendar"
              autoComplete="name"
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '1.2rem' }}>
            <label style={labelStyle}>Correo electrónico</label>
            <input
              type="email"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoComplete="email"
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '1.2rem' }}>
            <label style={labelStyle}>Contraseña</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={labelStyle}>Confirmar Contraseña</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '1.2rem' }}>
            <label style={labelStyle}>Nombre de la empresa</label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              required
              maxLength={255}
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '1.2rem' }}>
            <label style={labelStyle}>
              Descripción de la empresa{' '}
              <span style={{ color: theme.colors.textMuted }}>(opcional)</span>
            </label>
            <textarea
              value={companyDescription}
              onChange={(e) => setCompanyDescription(e.target.value)}
              rows={3}
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
            />
          </div>

          <div style={{ marginBottom: '1.2rem' }}>
            <label style={labelStyle}>Celular de contacto del encargado</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={labelStyle}>Zona horaria</label>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              required
              style={{ ...inputStyle, cursor: 'pointer' }}
            >
              {TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '1.2rem' }}>
            <label style={labelStyle}>
              País <span style={{ color: theme.colors.textMuted }}>(opcional)</span>
            </label>
            <select
              value={pais}
              onChange={(e) => setPais(e.target.value)}
              style={{ ...inputStyle, cursor: 'pointer' }}
            >
              <option value="">— Seleccionar país —</option>
              {AMERICAN_COUNTRIES.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>

          <div style={{
            marginBottom: '1.2rem',
            backgroundColor: `${theme.colors.primary}11`,
            border: `1px solid ${theme.colors.primary}55`,
            borderRadius: '8px',
            padding: '0.8rem 1rem',
          }}>
            <p style={{ color: theme.colors.text, fontSize: '0.82rem', lineHeight: 1.5, margin: 0 }}>
              📍 Tu empresa tendrá <strong>mejor cogida</strong> si llenas estos campos de ubicación.
              No son obligatorios: si quieres los llenas, si no, los puedes dejar en blanco.
            </p>
          </div>

          <LocationFields
            pais={pais}
            department={location.department}
            city={location.city}
            district={location.district}
            address={location.address}
            onChange={onLocationChange}
            inputStyle={inputStyle}
            labelStyle={labelStyle}
          />

          <div style={{
            marginBottom: '1.5rem',
            backgroundColor: theme.colors.background,
            borderRadius: '8px',
            border: `1px solid ${acceptedTerms ? theme.colors.primary : theme.colors.border}`,
            padding: '1rem',
            transition: 'border-color 0.2s',
          }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                style={{
                  marginTop: '2px',
                  width: '16px',
                  height: '16px',
                  accentColor: theme.colors.primary,
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              />
              <span style={{ color: theme.colors.text, fontSize: '0.85rem', lineHeight: '1.5' }}>
                He leído y acepto las{' '}
                <Link to="/terminos" target="_blank" rel="noopener noreferrer" style={{ color: theme.colors.primary, fontWeight: 700 }}>
                  Condiciones del Servicio
                </Link>{' '}
                y la{' '}
                <Link to="/privacidad" target="_blank" rel="noopener noreferrer" style={{ color: theme.colors.primary, fontWeight: 700 }}>
                  Política de Privacidad (Tratamiento de Datos)
                </Link>.
                Autorizo a New horizont start a recopilar, almacenar y usar mis datos personales con fines
                de prestación del servicio, de conformidad con la Ley 1581 de 2012 y demás normas aplicables.
              </span>
            </label>
          </div>

          {error && (
            <p style={{ color: theme.colors.error, textAlign: 'center', marginBottom: '1rem', fontSize: '0.9rem' }}>
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: canSubmit ? theme.colors.primary : theme.colors.border,
              color: theme.colors.background,
              border: 'none',
              borderRadius: '7px',
              fontSize: '1rem',
              fontWeight: '700',
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              transition: 'background-color 0.2s',
            }}
          >
            {loading ? 'Creando cuenta...' : 'Crear cuenta'}
          </button>

          {!acceptedTerms && (
            <p style={{ color: theme.colors.textMuted, textAlign: 'center', marginTop: '0.6rem', fontSize: '0.8rem' }}>
              Debes aceptar el tratamiento de datos para continuar
            </p>
          )}
        </form>

        <p style={{ color: theme.colors.textMuted, textAlign: 'center', marginTop: '1.5rem', fontSize: '0.9rem' }}>
          ¿Ya tienes cuenta?{' '}
          <Link to="/login" style={{ color: theme.colors.primary, textDecoration: 'none' }}>
            Inicia sesión
          </Link>
        </p>
      </div>
    </main>
  );
}

export default Register;
